/**
 * The repositories of a multi-repository project whose ingestion failed.
 * Each can be retried with a new copy of its archive (sent under the same
 * repository key, so it replaces only that repository) or removed. The other
 * repositories are never touched by either action.
 */
import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import {
  useRemoveProjectRepository,
  useRetryProjectRepository,
  type RepositoryRecord,
} from '../../../services/api';

export function failedKeyedRepositories(repos: RepositoryRecord[]): RepositoryRecord[] {
  return repos.filter((r) => r.repositoryKey && r.ingestionStatus === 'failed');
}

const FailedRepositoryRow: React.FC<{ projectId: string; repo: RepositoryRecord }> = ({ projectId, repo }) => {
  const input = useRef<HTMLInputElement>(null);
  const retry = useRetryProjectRepository(projectId);
  const remove = useRemoveProjectRepository(projectId);
  const [error, setError] = useState<string | null>(null);
  const busy = retry.isPending || remove.isPending;
  const name = repo.archiveName || repo.url;

  return (
    <Box sx={{ py: 0.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {name}
        </Typography>
        <Button size="small" disabled={busy} onClick={() => input.current?.click()}>
          {retry.isPending ? 'Uploading…' : 'Retry with archive…'}
        </Button>
        <Button
          size="small"
          color="error"
          disabled={busy}
          onClick={() => {
            if (!window.confirm(`Remove ${name} from this project? The other repositories are kept.`)) return;
            setError(null);
            remove.mutate(repo.id, { onError: (e) => setError(e instanceof Error ? e.message : 'Remove failed') });
          }}
        >
          {remove.isPending ? 'Removing…' : 'Remove'}
        </Button>
        <input
          ref={input}
          type="file"
          hidden
          accept=".zip,.tar,.tar.gz,.tgz,.gz"
          aria-label={`Replacement archive for ${name}`}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file || !repo.repositoryKey) return;
            setError(null);
            retry.mutate(
              { file, repositoryKey: repo.repositoryKey },
              { onError: (err) => setError(err instanceof Error ? err.message : 'Upload failed') },
            );
          }}
        />
      </Stack>
      {repo.ingestionError ? (
        <Typography variant="caption" color="text.secondary" component="div">
          {repo.ingestionError}
        </Typography>
      ) : null}
      {error ? (
        <Typography variant="caption" color="error" component="div">
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};

const FailedRepositoriesPanel: React.FC<{ projectId: string; repos: RepositoryRecord[] }> = ({ projectId, repos }) => {
  const failed = failedKeyedRepositories(repos);
  if (failed.length === 0) return null;
  return (
    <Alert severity="error" sx={{ mb: 1 }}>
      {failed.length} of {repos.length} repositories did not ingest. Retry each with a corrected archive, or remove
      it to continue with the others.
      {failed.map((repo) => (
        <FailedRepositoryRow key={repo.id} projectId={projectId} repo={repo} />
      ))}
    </Alert>
  );
};

export default FailedRepositoriesPanel;
